import { openPopup } from '@neb/popup';
import { css, html } from 'lit';
import moment from 'moment-timezone';

import NebTable, {
  ELEMENTS as BASE_ELEMENTS,
} from '../../../../packages/neb-lit-components/src/components/tables/neb-table';
import { POPUP_RENDER_KEYS } from '../../../../packages/neb-popup/src/renderer-keys';
import { MODE } from '../../../../packages/neb-utils/table';
import { formatTotals } from '../../../formatters/ar-hub';
import {
  CSS_SPACING,
  CSS_COLOR_GREY_3,
  CSS_FONT_WEIGHT_BOLD,
  CSS_SPACING_ROW_LARGE,
  CSS_BANNER_INFO_COLOR,
} from '../../../styles';
import { navigateToRow } from '../../../utils/navigation-util';

import '../../misc/neb-icon';
import '../../controls/inputs/neb-checkbox';
import '../../../../packages/neb-lit-components/src/components/controls/neb-button-icon';

export const ELEMENTS = {
  ...BASE_ELEMENTS,
  totalsHeader: { id: 'totals-header' },
  totalsFooter: { id: 'totals-footer' },
  invoiceLinks: { selector: '[id^=invoice-]' },
  followUpLinks: { selector: '[id^=follow-up-link-]' },
  caseLinks: { selector: '[id^=case-link-]' },
  eraEobLinks: { selector: '[id^=era-eob-]' },
  primaryPayer: { selector: '[id^=primary-payer-]' },
  secondaryPayer: { selector: '[id^=secondary-payer-]' },
  completeFollowUpButtons: { selector: '[id^=complete-follow-up-button-]' },
  adjustmentCodes: { selector: '[id^=adjustment-codes-]' },
};

class NebTableArHub extends NebTable {
  static get properties() {
    return {
      totals: Object,
      isInvoiceOverlayClosed: Boolean,
      lastClickedInvoice: Object,
      selectedTab: Object,
    };
  }

  initState() {
    super.initState();
    this.__formattedTotals = {};

    this.totals = {};
    this.isInvoiceOverlayClosed = false;
    this.lastClickedInvoice = null;
    this.selectedTab = null;
    this.showSelectAll = true;
    this.showBulkActionMenu = true;
    this.actionMenuItems = [
      {
        label: 'View Notes',
        onSelect: ({ index }) => this.onViewNotes(index),
      },
    ];

    this.selectedItems = [];
    this.mode = MODE.ACTION_MENU;

    this.onClickInvoice = () => {};

    this.onClickCase = () => {};

    this.onClickFollowUp = () => {};

    this.onCompleteFollowUp = () => {};

    this.onSetSelectedItems = () => {};

    this.onViewNotes = () => {};

    this.onPrimaryPayerClick = () => {};

    this.onSecondaryPayerClick = () => {};

    this.onClickERAsEOBs = () => {};
  }

  initHandlers() {
    super.initHandlers();

    this.handlers = {
      ...this.handlers,
      clickInvoice: e => {
        e.stopPropagation();
        const rowIndex = Number(e.currentTarget.getAttribute('index'));
        const row = this.model[rowIndex];

        this.lastClickedInvoice = {
          index: rowIndex,
          invoiceNumber: row.invoiceNumber,
        };

        this.onClickInvoice(row, rowIndex);
      },
      clickCase: e => {
        e.stopPropagation();
        const rowIndex = Number(e.currentTarget.getAttribute('index'));
        const row = this.model[rowIndex];

        this.onClickCase(row, rowIndex);
      },
      clickERAsEOBs: e => {
        e.stopPropagation();
        const rowIndex = Number(e.currentTarget.getAttribute('index'));
        const row = this.model[rowIndex];

        this.onClickERAsEOBs(row, rowIndex);
      },
      clickFollowUp: async rowIndex => {
        const row = this.model[rowIndex];

        const result = await openPopup(POPUP_RENDER_KEYS.AR_HUB_ADD_FOLLOW_UP, {
          id: row.followUpId,
          date: row.followUpDate
            ? moment(row.followUpDate, 'MM/DD/YYYY')
            : null,
          description: row.followUpDescription,
          parentId: row.invoiceId,
          selectedItems: [row],
          editMode: true,
        });

        if (result) {
          this.onClickFollowUp();
        }
      },
      completeFollowUp: rowIndex => {
        const row = this.model[rowIndex];

        this.onCompleteFollowUp({
          followUpId: row.followUpId,
          isCompleted: row.isFollowUpCompleted,
          rowIndex,
        });
      },
      clickPrimaryPayer: e => {
        const index = Number(e.currentTarget.getAttribute('index'));
        return this.onPrimaryPayerClick(index);
      },
      clickSecondaryPayer: e => {
        const index = Number(e.currentTarget.getAttribute('index'));
        return this.onSecondaryPayerClick(index);
      },
    };
  }

  update(changedProperties) {
    if (changedProperties.has('config') || changedProperties.has('totals')) {
      this.__formattedTotals = this.__getTotals();
    }

    super.update(changedProperties);

    if (
      changedProperties.has('isInvoiceOverlayClosed') &&
      this.isInvoiceOverlayClosed
    ) {
      this.__goToLastInvoiceVisited();
    }
  }

  static get styles() {
    return [
      super.styles,
      css`
        .header-container {
          background-color: white;
          position: sticky;
          z-index: 1;
          top: 0px;
        }

        .row-totals {
          display: flex;
          padding: 0 ${CSS_SPACING};
          background-color: ${CSS_COLOR_GREY_3};
          font-weight: ${CSS_FONT_WEIGHT_BOLD};
        }

        .row-totals .cell-header {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .cell-header {
          padding: ${CSS_SPACING_ROW_LARGE} 0;
        }

        .label {
          width: min-content;
          margin-right: 5px;
        }

        .container-arrow {
          display: flex;
          margin-left: 4px;
          align-self: center;
          align-items: center;
          justify-content: flex-end;
          flex-flow: column nowrap;
        }

        .cell:not(:last-child) {
          margin-right: 0px;
        }

        .cell[truncate] {
          display: inherit;
        }

        @keyframes fade {
          from {
            background: rgb(192, 230, 255);
          }
          to {
            background: transparent;
          }
        }

        .item-highlight {
          animation: fade 3s;
        }

        .cell-checkbox {
          padding: 5px 10px 5px 5px;
        }

        .icon {
          width: 28px;
          height: 28px;
          fill: ${CSS_BANNER_INFO_COLOR};
        }

        .icon-complete-follow-up {
          fill: #8cd211;
        }

        .follow-up-container {
          display: flex;
          align-items: flex-start;
          gap: 4px;
          width: 100%;
        }

        .text-wrap {
          flex: 1;
          word-break: break-word;
          white-space: normal;
        }

        .icon-container {
          display: flex;
          align-items: center;
          height: 100%;
        }

        .dotted-code:hover {
          border-bottom: 1px dotted;
          cursor: default;
        }
      `,
    ];
  }

  __goToLastInvoiceVisited() {
    if (!this.lastClickedInvoice) return;

    let rowIndex = this.lastClickedInvoice.index;

    if (
      this.model[rowIndex]?.invoiceNumber !==
      this.lastClickedInvoice.invoiceNumber
    ) {
      rowIndex = this.model.findIndex(
        row => row.invoiceNumber === this.lastClickedInvoice.invoiceNumber,
      );

      if (rowIndex === -1) return;
    }

    const row = this.shadowRoot.getElementById(`row-${rowIndex}`);

    if (row) {
      navigateToRow(row);
    }
  }

  __getTotals() {
    const firstColumnKey = this.config[0]?.key;

    return {
      [firstColumnKey]: 'Totals',
      ...formatTotals(this.totals),
    };
  }

  __truncateText(text, maxLength = 60) {
    if (!text || text.length <= maxLength) {
      return text;
    }

    return `${text.substring(0, maxLength)}...`;
  }

  __renderTotalsRow(id) {
    if (!this.model.length) {
      return '';
    }
    return html`
      <div id="${id}" class="row row-totals">
        ${this.config.map(columnConfig => {
          const totalValue = this.__formattedTotals[columnConfig.key];
          return html`
            <div class="cell cell-header" style="flex: ${columnConfig.flex}">
              ${totalValue}
            </div>
          `;
        })}

        <div class="cell cell-action-menu"></div>
      </div>
    `;
  }

  __renderHeader() {
    return html`
      <div class="header-container">
        ${super.__renderHeader()}
        ${this.__renderTotalsRow(ELEMENTS.totalsHeader.id)}
      </div>
    `;
  }

  __renderFollowUpButton(rowIndex) {
    const { isFollowUpCompleted, followUpId } = this.model[rowIndex];

    return followUpId
      ? html`
          <neb-button-icon
            id="complete-follow-up-button-${rowIndex}"
            icon=${isFollowUpCompleted
              ? 'neb:completeFollowUp'
              : 'neb:followUp'}
            class="${isFollowUpCompleted ? 'icon-complete-follow-up' : ''} icon"
            .onClick="${() => this.handlers.completeFollowUp(rowIndex)}"
          ></neb-button-icon>
        `
      : '';
  }

  __renderAdjustmentCodes({ value, rowIndex }) {
    if (!value) {
      return '';
    }

    const codesMap = this.model[rowIndex].adjustmentCodesMap;
    const codes = value.split(',').map(code => code.trim());
    const title = codes.map(code => `${code} - ${codesMap[code]}`).join('\n');

    return html`<span
      id="adjustment-codes-${rowIndex}"
      class="dotted-code"
      title="${title}"
      >${value}</span
    >`;
  }

  renderFooter() {
    return this.__renderTotalsRow(ELEMENTS.totalsFooter.id);
  }

  get __customCellRenderers() {
    return {
      invoiceNumber: data => this.__renderInvoiceNumberCell(data),
      followUpDate: data => this.__renderFollowUpCell(data),
      followUpDescription: data => this.__renderFollowUpCell(data),
      followUpDateAndDescription: data => this.__renderFollowUpCell(data),
      primaryPayer: data => this.__renderPrimaryPayerCell(data),
      secondaryPayer: data => this.__renderSecondaryPayerCell(data),
      adjustmentCodes: data => this.__renderAdjustmentCodes(data),
      case: data => this.__renderCaseCell(data),
      associatedERAsAndEOBs: data => this.__renderERAsAndEOBsCell(data),
    };
  }

  renderDataCell(value, columnConfig, rowIndex) {
    const renderer = this.__customCellRenderers[columnConfig.key];
    return renderer ? renderer({ value, columnConfig, rowIndex }) : value;
  }

  __renderERAsAndEOBsCell({ value, columnConfig, rowIndex }) {
    return html`
      <neb-text
        id="era-eob-${rowIndex}"
        key="${columnConfig.key}"
        bold
        link
        index="${rowIndex}"
        @click="${this.handlers.clickERAsEOBs}"
        >${value && value.length ? '✓' : ''}</neb-text
      >
    `;
  }

  __renderCaseCell({ value, columnConfig, rowIndex }) {
    return html`
      <neb-text
        id="case-link-${rowIndex}"
        key="${columnConfig.key}"
        bold
        link
        index="${rowIndex}"
        @click="${this.handlers.clickCase}"
        >${value}</neb-text
      >
    `;
  }

  __renderInvoiceNumberCell({ value, columnConfig, rowIndex }) {
    return html`
      <neb-text
        id="invoice-link-${rowIndex}"
        key="${columnConfig.key}"
        bold
        link
        index="${rowIndex}"
        @click="${this.handlers.clickInvoice}"
        >${value}</neb-text
      >
    `;
  }

  __renderFollowUpCell({ value, columnConfig, rowIndex }) {
    return html`
      <div class="follow-up-container">
        <neb-text
          id="follow-up-link-${rowIndex}"
          key="${columnConfig.key}"
          class="text-wrap"
          bold
          link
          index="${rowIndex}"
          @click="${() => this.handlers.clickFollowUp(rowIndex)}"
          >${this.__truncateText(value)}</neb-text
        >
        <div class="icon-container">
          ${this.__renderFollowUpButton(rowIndex)}
        </div>
      </div>
    `;
  }

  __renderPrimaryPayerCell({ value, columnConfig, rowIndex }) {
    return html`
      <neb-text
        id="primary-payer-${rowIndex}"
        key="${columnConfig.key}"
        bold
        link
        index="${rowIndex}"
        @click="${this.handlers.clickPrimaryPayer}"
        >${value}</neb-text
      >
    `;
  }

  __renderSecondaryPayerCell({ value, columnConfig, rowIndex }) {
    return html`
      <neb-text
        id="secondary-payer-${rowIndex}"
        key="${columnConfig.key}"
        bold
        link
        index="${rowIndex}"
        @click="${this.handlers.clickSecondaryPayer}"
        >${value}</neb-text
      >
    `;
  }
}

customElements.define('neb-table-ar-hub', NebTableArHub);
